/**
 * SKU Count — phone screen (Suri, 2026-10-16): "directly scan the SKU and update the stock, same logic as
 * Location count but instead of location they just directly scan the SKU". No location step: scan (or type)
 * a SKU, type the TOTAL physically in the warehouse for it (all locations added up), save. The backend
 * (services/stockCount.ts recordSkuStockCount) stores it as a pending count labelled "All locations"; a
 * Supervisor/Admin applies it from the admin Stock Counts page exactly like a location count.
 * Recounting the same SKU before it is applied replaces the earlier count ("latest scan wins").
 */
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, formatTime, qs } from '../dom.js';
import { signal } from '../feedback.js';
import type { StockCount } from '../types.js';

/** Must match ALL_LOCATIONS_LABEL in apps/worker/src/services/stockCount.ts. */
const ALL_LOCATIONS_LABEL = 'All locations';

const CAMERA_SCAN_HINTS = new Map<DecodeHintType, unknown>([
  [
    DecodeHintType.POSSIBLE_FORMATS,
    [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128],
  ],
]);

export async function renderSkuCount(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);

  div.innerHTML = `
    <div class="topbar">
      <h1>SKU Count</h1>
      <div class="actions-row"><button class="ghost" id="backBtn">Home</button><button class="ghost" id="refreshBtn">Refresh</button></div>
    </div>
    <div id="sessionSummary"></div>
    <p class="hint">Scan or type a SKU, then enter the TOTAL you can find in the whole warehouse. Tap a pending count to recount it, or ✕ to cancel a mistake.</p>
    <div class="last-scan" id="lastScan"><div class="sku">Ready</div><div class="count">Scan a SKU to begin</div></div>
    <div class="scan-input-wrap">
      <input type="text" id="scanInput" placeholder="Scan or type SKU / barcode" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
      <button type="button" class="ghost camera-btn" id="cameraBtn" aria-label="Scan with camera">📷</button>
    </div>
    <button class="ghost" id="manualSkuBtn" style="margin-top:0.5rem;">No barcode — enter SKU manually</button>
    <h2 style="margin-top:1.5rem;">Pending SKU counts (waiting for a supervisor to apply)</h2>
    <div class="list" id="pendingList"><p class="hint center">Loading…</p></div>
  `;

  const lastScanEl = qs<HTMLDivElement>(div, '#lastScan');
  const summaryEl = qs<HTMLDivElement>(div, '#sessionSummary');
  let pendingCache: StockCount[] = [];
  const scanInput = qs<HTMLInputElement>(div, '#scanInput');
  const cameraBtn = qs<HTMLButtonElement>(div, '#cameraBtn');
  const pendingListEl = qs<HTMLDivElement>(div, '#pendingList');
  let stopCameraScan: (() => void) | null = null;
  let destroyed = false;

  qs<HTMLButtonElement>(div, '#backBtn').addEventListener('click', () => {
    location.hash = '#/';
  });
  qs<HTMLButtonElement>(div, '#refreshBtn').addEventListener('click', () => void loadPending());
  qs<HTMLButtonElement>(div, '#manualSkuBtn').addEventListener('click', () => openManualSkuModal());

  function renderSummary(): void {
    const rows = pendingCache;
    if (rows.length === 0) {
      summaryEl.innerHTML = '';
      return;
    }
    const units = rows.reduce((n, c) => n + c.countedQuantity, 0);
    const differences = rows.filter((c) => c.mintsoftOnHandAtCount !== null && c.mintsoftOnHandAtCount !== c.countedQuantity).length;
    summaryEl.innerHTML = `<div class="sc-summary">
      <div><div class="n">${rows.length}</div><div class="l">SKUs counted</div></div>
      <div><div class="n">${units}</div><div class="l">units</div></div>
      <div class="${differences > 0 ? 'diff' : ''}"><div class="n">${differences}</div><div class="l">differ from Mintsoft</div></div>
    </div>`;
  }

  function focusInput(): void {
    if (destroyed) return;
    scanInput.focus({ preventScroll: true });
  }

  function flash(kind: 'good' | 'warn' | 'bad' | 'neutral', title: string, subtitle: string): void {
    lastScanEl.classList.remove('flash-good', 'flash-bad', 'result-good', 'result-warn', 'result-bad');
    void lastScanEl.offsetWidth; // force reflow so repeated identical flashes still animate
    if (kind !== 'neutral') lastScanEl.classList.add(`result-${kind}`);
    lastScanEl.innerHTML = `<div class="sku">${escapeHtml(title)}</div><div class="count">${escapeHtml(subtitle)}</div>`;
    signal(kind);
  }

  async function loadPending(): Promise<void> {
    if (pendingCache.length === 0) pendingListEl.innerHTML = '<p class="hint center">Loading…</p>';
    try {
      const all = await api.listStockCounts('PENDING');
      // Only SKU counts here — per-location counts belong to the Location Count screen.
      pendingCache = all.filter((c) => c.locationName === ALL_LOCATIONS_LABEL);
      renderSummary();
      renderPending(pendingCache);
    } catch (err) {
      pendingListEl.innerHTML = `<p class="error-text">${escapeHtml(describeError(err))}</p>`;
    }
  }

  function renderPending(pending: StockCount[]): void {
    pendingListEl.innerHTML = '';
    if (pending.length === 0) {
      pendingListEl.innerHTML = '<p class="hint">Nothing pending.</p>';
      return;
    }
    const sorted = [...pending].sort((a, b) => b.countedAt.localeCompare(a.countedAt));
    for (const c of sorted) {
      const variance = c.mintsoftOnHandAtCount === null ? null : c.countedQuantity - c.mintsoftOnHandAtCount;
      const varianceText = variance === null ? '' : ` <span class="expected">(Mintsoft ${c.mintsoftOnHandAtCount}, ${variance > 0 ? '+' : ''}${variance})</span>`;
      const row = document.createElement('div');
      row.className = 'item-row';
      row.innerHTML = `
        <div class="pending-main" style="cursor:pointer;">
          <div class="sku">${escapeHtml(c.sku)}</div>
          <div class="ean">${formatTime(c.countedAt)} · tap to recount</div>
        </div>
        <div class="qty">${c.countedQuantity}${varianceText}</div>
        <button type="button" class="ghost cancel-pending" aria-label="Cancel this pending count" style="margin-left:0.5rem;">&times;</button>
      `;
      qs<HTMLDivElement>(row, '.pending-main').addEventListener('click', () => openQuantityModal({ sku: c.sku }));
      qs<HTMLButtonElement>(row, '.cancel-pending').addEventListener('click', (e) => {
        e.stopPropagation();
        void cancelPending(c.id, c.sku);
      });
      pendingListEl.appendChild(row);
    }
  }

  async function cancelPending(id: string, sku: string): Promise<void> {
    if (!confirm(`Cancel the pending count for ${sku}?`)) return;
    try {
      await api.cancelStockCount(id);
      await loadPending();
    } catch (err) {
      flash('bad', 'Error', describeError(err));
    }
  }

  async function submitCount(input: { ean?: string; sku?: string }, countedQuantity: number): Promise<void> {
    try {
      const outcome = await api.recordSkuCount({ ...input, countedQuantity });
      if (outcome.kind === 'PRODUCT_NOT_FOUND') {
        flash('bad', 'Not found', input.ean ? `No product for barcode ${input.ean}` : `No product for SKU ${input.sku}`);
        return;
      }
      if (outcome.kind !== 'RECORDED') {
        flash('bad', 'Error', 'Unexpected answer from the server.');
        return;
      }
      const { stockCount, supersededPreviousId } = outcome;
      const variance = stockCount.mintsoftOnHandAtCount === null ? null : stockCount.countedQuantity - stockCount.mintsoftOnHandAtCount;
      const replaced = supersededPreviousId ? ' · replaced earlier count' : '';
      if (variance === null) {
        flash('good', `Saved: ${stockCount.sku}`, `Counted ${stockCount.countedQuantity} in total${replaced}`);
      } else if (variance === 0) {
        flash('good', `✓ ${stockCount.sku}`, `Counted ${stockCount.countedQuantity} — matches Mintsoft${replaced}`);
      } else {
        flash('warn', `Saved: ${stockCount.sku}`, `Counted ${stockCount.countedQuantity} · Mintsoft ${stockCount.mintsoftOnHandAtCount} (${variance > 0 ? '+' : ''}${variance})${replaced}`);
      }
      void loadPending();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PRODUCT_NOT_FOUND') {
        flash('bad', 'Not found', input.ean ? `No product for barcode ${input.ean}` : `No product for SKU ${input.sku}`);
        return;
      }
      flash('bad', 'Error', describeError(err));
    }
  }

  function openQuantityModal(input: { ean?: string; sku?: string }): void {
    const label = input.ean ? `Barcode ${input.ean}` : `SKU ${input.sku}`;
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2 id="qtyTitle">${escapeHtml(label)}</h2>
        <p class="hint" id="qtyProduct" style="margin:0;"></p>
        <div class="expected-box" id="expectedBox">Checking Mintsoft…</div>
        <label for="countedQty">How many in TOTAL (all locations)?</label>
        <div class="qty-row">
          <button type="button" class="ghost step" id="qtyMinus" aria-label="One less">&minus;</button>
          <input type="number" id="countedQty" min="0" step="1" inputmode="numeric" class="qty-input" autofocus />
          <button type="button" class="ghost step" id="qtyPlus" aria-label="One more">+</button>
        </div>
        <button class="match big" id="matchBtn" style="display:none;"></button>
        <p id="qtyStatus" class="hint"></p>
        <div class="actions-row">
          <button class="ghost big" id="cancelQty">Cancel</button>
          <button class="primary big" id="submitQty">Save count</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    let closed = false;
    const close = () => {
      closed = true;
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelQty').addEventListener('click', close);

    const qtyInput = qs<HTMLInputElement>(backdrop, '#countedQty');
    const expectedBox = qs<HTMLDivElement>(backdrop, '#expectedBox');
    const matchBtn = qs<HTMLButtonElement>(backdrop, '#matchBtn');
    const submitBtn = qs<HTMLButtonElement>(backdrop, '#submitQty');
    const status = qs<HTMLParagraphElement>(backdrop, '#qtyStatus');
    qtyInput.focus();

    qs<HTMLButtonElement>(backdrop, '#qtyMinus').addEventListener('click', () => {
      qtyInput.value = String(Math.max(0, (Number(qtyInput.value) || 0) - 1));
    });
    qs<HTMLButtonElement>(backdrop, '#qtyPlus').addEventListener('click', () => {
      qtyInput.value = String((Number(qtyInput.value) || 0) + 1);
    });

    // Ask Mintsoft what it expects in total. The keypad is usable immediately; this only fills in the hint
    // and catches a mis-scanned barcode before a quantity is typed.
    void api
      .expectedSkuCount(input)
      .then((info) => {
        if (closed) return;
        qs<HTMLHeadingElement>(backdrop, '#qtyTitle').textContent = info.sku;
        if (info.productName) qs<HTMLParagraphElement>(backdrop, '#qtyProduct').textContent = info.productName;
        if (info.expectedQuantity === null) {
          expectedBox.className = 'expected-box unknown';
          expectedBox.textContent = 'Mintsoft quantity unavailable — count what you see.';
        } else {
          const expected = info.expectedQuantity;
          const where = info.locations.length > 0 ? `<div class="hint" style="margin:0.25rem 0 0 0;">${info.locations.map((l) => `${escapeHtml(l.location)} ${l.quantity}`).join(' · ')}</div>` : '';
          expectedBox.className = 'expected-box';
          expectedBox.innerHTML = `Mintsoft expects <strong>${expected}</strong> in total${where}`;
          matchBtn.style.display = '';
          matchBtn.textContent = `✓ Matches — ${expected}`;
          matchBtn.addEventListener('click', () => void submit(expected));
        }
        if (info.pendingCountedQuantity !== null) {
          status.textContent = `You already counted ${info.pendingCountedQuantity} (waiting to be applied) — saving replaces it.`;
        }
      })
      .catch((err) => {
        if (closed) return;
        if (err instanceof ApiError && err.code === 'PRODUCT_NOT_FOUND') {
          expectedBox.className = 'expected-box bad';
          expectedBox.textContent = input.ean ? `No product found for barcode ${input.ean}. Check the scan.` : `No product found for SKU ${input.sku}.`;
          submitBtn.disabled = true;
          return;
        }
        expectedBox.className = 'expected-box unknown';
        expectedBox.textContent = `Could not check Mintsoft (${describeError(err)}) — you can still count.`;
      });

    async function submit(forced?: number): Promise<void> {
      const value = forced ?? (qtyInput.value.trim() === '' ? NaN : Number(qtyInput.value));
      if (!Number.isInteger(value) || value < 0) {
        status.classList.add('error-text');
        status.textContent = 'Enter a whole number, zero or greater.';
        return;
      }
      if (value > 100000) {
        status.classList.add('error-text');
        status.textContent = `${value} looks wrong — did a barcode get typed into the quantity box? Clear it and type the number you counted.`;
        qtyInput.select();
        return;
      }
      status.classList.remove('error-text');
      status.textContent = 'Saving…';
      close(); // straight back to scanning — the result shows in the big banner above
      flash('neutral', 'Saving…', `${input.sku ?? input.ean ?? ''} · ${value}`);
      await submitCount(input, value);
    }

    submitBtn.addEventListener('click', () => void submit());
    qtyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void submit();
      }
    });
  }

  function openManualSkuModal(): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal">
        <h2>Enter SKU</h2>
        <label for="manualSku">SKU</label>
        <input type="text" id="manualSku" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
        <div class="actions-row">
          <button class="ghost big" id="cancelSku">Cancel</button>
          <button class="primary big" id="nextSku">Next</button>
        </div>
      </div>
    `;
    document.body.appendChild(backdrop);
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelSku').addEventListener('click', close);
    const skuInput = qs<HTMLInputElement>(backdrop, '#manualSku');
    skuInput.focus();

    function next(): void {
      const sku = skuInput.value.trim();
      if (!sku) return;
      close();
      openQuantityModal({ sku });
    }
    qs<HTMLButtonElement>(backdrop, '#nextSku').addEventListener('click', next);
    skuInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        next();
      }
    });
  }

  scanInput.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = scanInput.value.trim();
    scanInput.value = '';
    if (!value) return;
    openQuantityModal({ ean: value });
  });
  scanInput.addEventListener('blur', () => {
    // Same re-focus-shortly-after-blur pattern as scan.ts, so the
    // hardware scanner's keyboard-emulated input keeps landing here
    // without the operator having to tap back in.
    setTimeout(() => {
      if (!document.querySelector('.modal-backdrop')) focusInput();
    }, 250);
  });
  focusInput();

  cameraBtn.addEventListener('click', () => openCameraScanModal());

  function openCameraScanModal(): void {
    if (!navigator.mediaDevices?.getUserMedia) {
      const backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop';
      backdrop.innerHTML = `
        <div class="modal">
          <h2>Camera scanning unavailable</h2>
          <p>This browser doesn't support camera access. Keep using the handheld scanner or type the barcode in.</p>
          <button class="ghost" id="closeBtn">Close</button>
        </div>
      `;
      document.body.appendChild(backdrop);
      const close = () => {
        backdrop.remove();
        focusInput();
      };
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) close();
      });
      qs<HTMLButtonElement>(backdrop, '#closeBtn').addEventListener('click', close);
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <div class="modal camera-modal">
        <h2>Scan with Camera</h2>
        <p class="hint" id="cameraStatus">Starting camera…</p>
        <video id="cameraVideo" autoplay playsinline muted></video>
        <button class="ghost" id="cancelCamera" style="margin-top:0.75rem;">Cancel</button>
      </div>
    `;
    document.body.appendChild(backdrop);

    const video = qs<HTMLVideoElement>(backdrop, '#cameraVideo');
    const statusEl = qs<HTMLParagraphElement>(backdrop, '#cameraStatus');
    let controls: { stop: () => void } | null = null;
    let stopped = false;

    const stop = () => {
      if (stopped) return;
      stopped = true;
      controls?.stop();
      stopCameraScan = null;
    };
    const close = () => {
      stop();
      backdrop.remove();
      focusInput();
    };
    stopCameraScan = close;

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelCamera').addEventListener('click', close);

    const reader = new BrowserMultiFormatReader(CAMERA_SCAN_HINTS);
    let handled = false;

    void (async () => {
      try {
        controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          video,
          (result, err) => {
            if (stopped || handled) return;
            if (result) {
              handled = true;
              const value = result.getText();
              close();
              openQuantityModal({ ean: value });
              return;
            }
            void err; // fires every frame with no barcode found yet — not a real failure
          },
        );
        if (stopped) {
          controls.stop();
          return;
        }
        statusEl.textContent = 'Point the camera at a barcode.';
      } catch {
        statusEl.classList.add('error-text');
        statusEl.textContent = "Couldn't access the camera. Check permissions and try again.";
      }
    })();
  }

  void loadPending();

  const cleanup = () => {
    destroyed = true;
    stopCameraScan?.();
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Network error.';
  if (err instanceof ApiError) return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  return String(err);
}
